import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MeetingSettings, MediaDevice } from '../types';
import { getMicrophones, getCameras, getSpeakers } from '../services/webrtc/mediaDevices';
import { applyDeviceSelection, getRoom } from '../services/livekit';

interface SettingsStore extends MeetingSettings {
  availableMicrophones: MediaDevice[];
  availableCameras: MediaDevice[];
  availableSpeakers: MediaDevice[];

  loadDevices: () => Promise<void>;
  setMicrophoneDevice: (deviceId: string) => void;
  setCameraDevice: (deviceId: string) => void;
  setSpeakerDevice: (deviceId: string) => void;
  setMuted: (muted: boolean) => void;
  setCameraOn: (on: boolean) => void;
}

/**
 * Settings state management store.
 * Persisted to localStorage.
 */
export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      microphoneDeviceId: '',
      cameraDeviceId: '',
      speakerDeviceId: '',
      isMuted: false,
      isCameraOn: false,
      availableMicrophones: [],
      availableCameras: [],
      availableSpeakers: [],

      loadDevices: async () => {
        const [microphones, cameras, speakers] = await Promise.all([
          getMicrophones(),
          getCameras(),
          getSpeakers(),
        ]);

        const current = get();
        const nextMicrophoneId = resolveDeviceId(
          current.microphoneDeviceId,
          microphones
        );
        const nextCameraId = resolveDeviceId(current.cameraDeviceId, cameras);
        const nextSpeakerId = resolveDeviceId(current.speakerDeviceId, speakers);

        set({
          availableMicrophones: microphones,
          availableCameras: cameras,
          availableSpeakers: speakers,
          microphoneDeviceId: nextMicrophoneId,
          cameraDeviceId: nextCameraId,
          speakerDeviceId: nextSpeakerId,
        });

        applyDeviceSelectionFromValues(nextMicrophoneId, nextCameraId, nextSpeakerId);
      },

      setMicrophoneDevice: (deviceId: string) => {
        set({ microphoneDeviceId: deviceId });
        const { cameraDeviceId, speakerDeviceId } = get();
        applyDeviceSelectionFromValues(deviceId, cameraDeviceId, speakerDeviceId);
      },

      setCameraDevice: (deviceId: string) => {
        set({ cameraDeviceId: deviceId });
        const { microphoneDeviceId, speakerDeviceId } = get();
        applyDeviceSelectionFromValues(microphoneDeviceId, deviceId, speakerDeviceId);
      },

      setSpeakerDevice: (deviceId: string) => {
        set({ speakerDeviceId: deviceId });
        const { microphoneDeviceId, cameraDeviceId } = get();
        applyDeviceSelectionFromValues(microphoneDeviceId, cameraDeviceId, deviceId);
      },

      setMuted: (muted: boolean) => {
        set({ isMuted: muted });
      },

      setCameraOn: (on: boolean) => {
        set({ isCameraOn: on });
      },
    }),
    {
      name: 'war-room-settings',
      partialize: (state) => ({
        microphoneDeviceId: state.microphoneDeviceId,
        cameraDeviceId: state.cameraDeviceId,
        speakerDeviceId: state.speakerDeviceId,
      }),
    }
  )
);

function applyDeviceSelectionFromValues(
  microphoneDeviceId: string,
  cameraDeviceId: string,
  speakerDeviceId: string
): void {
  void applyDeviceSelection(getRoom(), {
    microphoneDeviceId: microphoneDeviceId || undefined,
    cameraDeviceId: cameraDeviceId || undefined,
    speakerDeviceId: speakerDeviceId || undefined,
  });
}

function resolveDeviceId(currentId: string, devices: MediaDevice[]): string {
  if (currentId && devices.some((device) => device.deviceId === currentId)) {
    return currentId;
  }

  return devices[0]?.deviceId || '';
}
